import React, { useEffect, useRef } from "react";
import { Stack, Title, Text, Box } from "@mantine/core";
import { QRCodeDisplay } from "@/components/generic";

export interface ActivationQRCodeViewProps {
  /** The activation code to display in QR code */
  activationCode: string;

  /** Function to call for polling activation status */
  onPollActivation?: ((activationCode: string) => Promise<void>) | undefined;

  /** Polling interval in milliseconds */
  pollingInterval?: number;

  /** Whether polling is currently active */
  isPolling?: boolean;
}

export const ActivationQRCodeView: React.FC<ActivationQRCodeViewProps> = ({
  activationCode,
  onPollActivation,
  pollingInterval = 5000,
  isPolling = true,
}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isPolling) {
      return;
    }

    console.log(
      "ActivationQRCodeView useEffect",
      activationCode,
      onPollActivation,
      pollingInterval
    );

    // Set up interval for repeated polling
    intervalRef.current = setInterval(() => {
      if (typeof onPollActivation === "function") {
        onPollActivation(activationCode);
      }
    }, pollingInterval);

    // Cleanup interval on unmount or when polling stops
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [activationCode, onPollActivation, pollingInterval]);

  return (
    <Stack align="center" justify="center" gap="xl" style={{ height: "100%" }}>
      <Box ta="center">
        <Title order={1} size="h2" mb="md" c="gray.8">
          Activate Your Display
        </Title>
        <Text size="lg" c="dimmed" mb="xl">
          Scan the QR code below with your mobile device to complete activation
        </Text>
      </Box>

      <QRCodeDisplay activationCode={activationCode} size={250} />

      <Stack align="center" gap="xs" ta="center">
        <Text size="md" fw={600} c="blue.6">
          Activation Code: {activationCode}
        </Text>
        <Text size="sm" c="dimmed">
          Waiting for activation confirmation...
        </Text>
      </Stack>
    </Stack>
  );
};
